import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Button,
  Spinner,
  Alert,
  Badge,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import Swal from "sweetalert2";
import HeadersRole from "../../../Components/Headers/HeadersRole";
import {
  getFailedPayments,
  cancelImmediatelySubscription,
  sendFailedPaymentNotification,
  processFailedPayments,
  retryFailedPayment,
  clearFailedPaymentStatus,
} from "../../../slices/admin/thunk";
import { formatDate } from "../../../utils/utils";
import scheduleIcon from "../../../assets/images/iconsRoles/nationalIcon.svg";
import { useNavigate } from "react-router-dom";

const STEP_CONFIG = {
  1: { label: "Step 1", sublabel: "Day 0", color: "success", bgClass: "bg-soft-success" },
  2: { label: "Step 2", sublabel: "Day 3", color: "success", bgClass: "bg-soft-success" },
  3: { label: "Step 3", sublabel: "Day 8", color: "warning", bgClass: "bg-soft-warning" },
  4: { label: "Step 4", sublabel: "Day 15", color: "warning", bgClass: "bg-soft-warning" },
  5: { label: "Step 5", sublabel: "Day 16", color: "danger", bgClass: "bg-soft-danger" },
};

const ScheduleMonitoring = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stepFilter, setStepFilter] = useState("");
  const [page, setPage] = useState(0);

  // Cancel modal state
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [userToCancel, setUserToCancel] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  // Clear modal state
  const [clearModalOpen, setClearModalOpen] = useState(false);
  const [userToClear, setUserToClear] = useState(null);
  const [clearReason, setClearReason] = useState("");
  const [clearing, setClearing] = useState(false);

  // Action states
  const [notifyingUserId, setNotifyingUserId] = useState(null);
  const [retryingUserId, setRetryingUserId] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchData();
  }, [stepFilter, page]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { page, limit: 50 };
      if (stepFilter) params.step = parseInt(stepFilter);
      const response = await dispatch(getFailedPayments(params)).unwrap();
      setData(response);
    } catch (err) {
      setError(err.message || "Failed to fetch data");
      console.error("Error fetching failed payments:", err);
    } finally {
      setLoading(false);
    }
  };

  // Cancel handlers
  const handleOpenCancelModal = (user) => {
    setUserToCancel(user);
    setCancelModalOpen(true);
  };

  const handleCloseCancelModal = () => {
    setUserToCancel(null);
    setCancelModalOpen(false);
  };

  const handleConfirmCancel = async () => {
    if (!userToCancel) return;

    setCancelling(true);
    try {
      await dispatch(cancelImmediatelySubscription({
        userId: userToCancel.id,
        immediate: true
      })).unwrap();

      Swal.fire("Cancelled!", `User ${userToCancel.email} cancelled successfully`, "success");
      handleCloseCancelModal();
      fetchData();
    } catch (err) {
      Swal.fire("Error!", err.message || "Failed to cancel user", "error");
      console.error("Error cancelling user:", err);
    } finally {
      setCancelling(false);
    }
  };

  // Clear handlers
  const handleOpenClearModal = (user) => {
    setUserToClear(user);
    setClearReason("");
    setClearModalOpen(true);
  };

  const handleCloseClearModal = () => {
    setUserToClear(null);
    setClearReason("");
    setClearModalOpen(false);
  };

  const handleConfirmClear = async () => {
    if (!userToClear || !clearReason.trim()) {
      Swal.fire("Error!", "Please provide a reason", "warning");
      return;
    }

    setClearing(true);
    try {
      await dispatch(clearFailedPaymentStatus({
        userId: userToClear.id,
        reason: clearReason.trim()
      })).unwrap();

      Swal.fire("Cleared!", `Failed payment status cleared for ${userToClear.email}`, "success");
      handleCloseClearModal();
      fetchData();
    } catch (err) {
      Swal.fire("Error!", err.message || "Failed to clear status", "error");
      console.error("Error clearing status:", err);
    } finally {
      setClearing(false);
    }
  };

  // Notification handler
  const handleSendNotification = async (user) => {
    setNotifyingUserId(user.id);
    try {
      await dispatch(sendFailedPaymentNotification({ userId: user.id })).unwrap();
      Swal.fire("Sent!", `Notification sent to ${user.email}`, "success");
      fetchData();
    } catch (err) {
      Swal.fire("Error!", err.message || "Failed to send notification", "error");
      console.error("Error sending notification:", err);
    } finally {
      setNotifyingUserId(null);
    }
  };

  // Retry payment handler
  const handleRetryPayment = async (user) => {
    const result = await Swal.fire({
      title: 'Retry Payment?',
      text: `This will attempt to charge ${user.email} again.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#0ab39c',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, retry payment',
    });

    if (!result.isConfirmed) return;

    setRetryingUserId(user.id);
    try {
      const response = await dispatch(retryFailedPayment({ userId: user.id })).unwrap();
      Swal.fire("Success!", response.message || "Payment retry initiated", "success");
      fetchData();
    } catch (err) {
      Swal.fire("Error!", err.message || "Failed to retry payment", "error");
      console.error("Error retrying payment:", err);
    } finally {
      setRetryingUserId(null);
    }
  };

  // Process cronjob handler
  const handleProcessCronjob = async () => {
    const result = await Swal.fire({
      title: 'Run Failed Payments Cronjob?',
      text: 'This will process all failed payments and advance steps as needed.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f06548',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, run it',
    });

    if (!result.isConfirmed) return;

    setProcessing(true);
    try {
      const response = await dispatch(processFailedPayments()).unwrap();

      // Show detailed report
      const report = response.report || {};
      const summary = report.summary || {};

      Swal.fire({
        title: 'Cronjob Completed!',
        html: `
          <div class="text-start">
            <p><strong>Processed:</strong> ${summary.totalProcessed || 0} users</p>
            <p><strong>Retried:</strong> ${summary.retriedCount || 0}</p>
            <p><strong>Advanced:</strong> ${summary.advancedCount || 0}</p>
            <p><strong>Cancelled:</strong> ${summary.cancelledCount || 0}</p>
          </div>
        `,
        icon: 'success',
      });

      fetchData();
    } catch (err) {
      Swal.fire("Error!", err.message || "Failed to process cronjob", "error");
      console.error("Error processing cronjob:", err);
    } finally {
      setProcessing(false);
    }
  };

  const getDaysSinceFailure = (firstFailedDate) => {
    if (!firstFailedDate) return "N/A";
    const now = new Date();
    const failedDate = new Date(firstFailedDate);
    const diffTime = Math.abs(now - failedDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `${diffDays} days`;
  };

  const handleStepFilterChange = (e) => {
    setStepFilter(e.target.value);
    setPage(0);
  };

  const scheduleLabel = (
    <span className="text-muted">
      View users with failed payments and manage cancellations
    </span>
  );

  if (loading && !data) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <Spinner color="primary" size="lg" />
            <p className="mt-3 mb-0">Loading failed payments...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="danger">
            <i className="ri-error-warning-line me-2"></i>
            {error}
            <Button color="primary" size="sm" className="ms-3" onClick={fetchData}>
              Retry
            </Button>
          </Alert>
        </Container>
      </div>
    );
  }

  const stats = data?.stats || {};

  return (
    <div className="page-content">
      <HeadersRole
        role={"Failed Payments"}
        icon={scheduleIcon}
        svg={true}
        text={scheduleLabel}
      />

      <Container fluid>
        {/* Action Buttons */}
        <Row className="mb-3">
          <Col className="d-flex justify-content-end gap-2">
            <Button
              color="danger"
              onClick={handleProcessCronjob}
              disabled={processing}
            >
              {processing ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Processing...
                </>
              ) : (
                <>
                  <i className="ri-play-circle-line me-1"></i>
                  Run Cronjob
                </>
              )}
            </Button>
          </Col>
        </Row>

        {/* Stats Cards - Steps 1-5 + Total */}
        <Row className="mb-4">
          {[1, 2, 3, 4, 5].map((step) => {
            const config = STEP_CONFIG[step];
            return (
              <Col key={step} md={2}>
                <Card
                  className={`card-animate cursor-pointer ${stepFilter === String(step) ? 'border-primary border-2' : ''}`}
                  onClick={() => setStepFilter(stepFilter === String(step) ? "" : String(step))}
                >
                  <CardBody className="p-3">
                    <div className="d-flex align-items-center">
                      <div className="flex-grow-1">
                        <p className="text-uppercase fw-medium text-muted mb-0 fs-12">{config.label}</p>
                        <p className="text-muted mb-0 fs-11">{config.sublabel}</p>
                      </div>
                    </div>
                    <div className="d-flex align-items-end justify-content-between mt-2">
                      <h4 className={`fs-22 fw-semibold ff-secondary mb-0 text-${config.color}`}>
                        {stats[`step${step}Count`] || 0}
                      </h4>
                      <div className="avatar-sm flex-shrink-0">
                        <span className={`avatar-title ${config.bgClass} rounded fs-3`}>
                          <i className={`ri-error-warning-line text-${config.color}`}></i>
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            );
          })}
          <Col md={2}>
            <Card
              className={`card-animate cursor-pointer ${stepFilter === "" ? 'border-primary border-2' : ''}`}
              onClick={() => setStepFilter("")}
            >
              <CardBody className="p-3">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <p className="text-uppercase fw-medium text-muted mb-0 fs-12">Total</p>
                    <p className="text-muted mb-0 fs-11">All Steps</p>
                  </div>
                </div>
                <div className="d-flex align-items-end justify-content-between mt-2">
                  <h4 className="fs-22 fw-semibold ff-secondary mb-0 text-primary">
                    {stats.total || 0}
                  </h4>
                  <div className="avatar-sm flex-shrink-0">
                    <span className="avatar-title bg-soft-primary rounded fs-3">
                      <i className="ri-user-line text-primary"></i>
                    </span>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Users Table */}
        <Row>
          <Col>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-3">
                  <h5 className="card-title mb-0">
                    Users with Failed Payments
                    {data?.users?.length > 0 && (
                      <Badge color="secondary" className="ms-2">
                        {data.pagination?.total || data.users.length}
                      </Badge>
                    )}
                  </h5>
                  <Input
                    type="select"
                    className="form-select-sm"
                    style={{ width: '150px' }}
                    value={stepFilter}
                    onChange={handleStepFilterChange}
                  >
                    <option value="">All Steps</option>
                    <option value="1">Step 1 (Day 0)</option>
                    <option value="2">Step 2 (Day 3)</option>
                    <option value="3">Step 3 (Day 8)</option>
                    <option value="4">Step 4 (Day 15)</option>
                    <option value="5">Step 5 (Day 16)</option>
                  </Input>
                </div>
                <Button color="primary" size="sm" onClick={fetchData} disabled={loading}>
                  {loading ? <Spinner size="sm" /> : <i className="ri-refresh-line me-1"></i>}
                  Refresh
                </Button>
              </CardHeader>
              <CardBody>
                {!data?.users || data.users.length === 0 ? (
                  <Alert color="success" className="mb-0">
                    <i className="ri-checkbox-circle-line me-2"></i>
                    No users with failed payments found.
                  </Alert>
                ) : (
                  <div className="table-responsive">
                    <Table className="table-hover table-nowrap align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Email</th>
                          <th>Step</th>
                          <th>Days Since Failure</th>
                          <th>First Failed</th>
                          <th>Failed Count</th>
                          <th>Status</th>
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.users.map((user) => {
                          const step = user.failedPaymentStep || 1;
                          const stepConfig = STEP_CONFIG[step] || STEP_CONFIG[1];
                          const isActioning = notifyingUserId === user.id || retryingUserId === user.id;

                          return (
                            <tr
                              key={user.id}
                              className={`cursor-pointer ${step >= 4 ? "table-warning" : ""} ${step === 5 ? "table-danger" : ""}`}
                              onClick={() => navigate(`/admin/member-management/${user.id}`)}
                            >
                              <td>
                                <strong>{user.email}</strong>
                              </td>
                              <td>
                                <Badge color={stepConfig.color} className="fs-6">
                                  Step {step}
                                </Badge>
                              </td>
                              <td>
                                {getDaysSinceFailure(user.firstFailedPaymentDate)}
                              </td>
                              <td>
                                {user.firstFailedPaymentDate ? (
                                  <small>{formatDate(user.firstFailedPaymentDate)}</small>
                                ) : (
                                  <small className="text-muted">N/A</small>
                                )}
                              </td>
                              <td>
                                <Badge color={user.failedPaymentsCount >= 3 ? "danger" : "warning"}>
                                  {user.failedPaymentsCount || 0}
                                </Badge>
                              </td>
                              <td>
                                <Badge
                                  color={user.subscriptionStatus === "active" ? "success" : "danger"}
                                >
                                  {user.subscriptionStatus || "unknown"}
                                </Badge>
                              </td>
                              <td className="text-center" onClick={(e) => e.stopPropagation()}>
                                {user.subscriptionStatus === "active" ? (
                                  <UncontrolledDropdown>
                                    <DropdownToggle
                                      tag="button"
                                      className="btn btn-soft-secondary btn-sm"
                                      disabled={isActioning}
                                    >
                                      {isActioning ? <Spinner size="sm" /> : <i className="ri-more-2-fill"></i>}
                                    </DropdownToggle>
                                    <DropdownMenu className="dropdown-menu-end">
                                      <DropdownItem onClick={() => handleRetryPayment(user)}>
                                        <i className="ri-refresh-line me-2 text-success"></i>
                                        Retry Payment
                                      </DropdownItem>
                                      <DropdownItem onClick={() => handleSendNotification(user)}>
                                        <i className="ri-mail-send-line me-2 text-warning"></i>
                                        Send Reminder
                                      </DropdownItem>
                                      <DropdownItem onClick={() => handleOpenClearModal(user)}>
                                        <i className="ri-eraser-line me-2 text-info"></i>
                                        Clear Status
                                      </DropdownItem>
                                      <DropdownItem divider />
                                      <DropdownItem onClick={() => handleOpenCancelModal(user)} className="text-danger">
                                        <i className="ri-close-circle-line me-2"></i>
                                        Cancel Subscription
                                      </DropdownItem>
                                    </DropdownMenu>
                                  </UncontrolledDropdown>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}

                {/* Pagination */}
                {data?.pagination && data.pagination.pages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <small className="text-muted">
                      Page {page + 1} of {data.pagination.pages} ({data.pagination.total} total)
                    </small>
                    <div className="d-flex gap-2">
                      <Button
                        color="light"
                        size="sm"
                        disabled={page === 0}
                        onClick={() => setPage(page - 1)}
                      >
                        Previous
                      </Button>
                      <Button
                        color="light"
                        size="sm"
                        disabled={page >= data.pagination.pages - 1}
                        onClick={() => setPage(page + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Cancel Confirmation Modal */}
      <Modal isOpen={cancelModalOpen} toggle={handleCloseCancelModal} centered>
        <ModalHeader toggle={handleCloseCancelModal} className="bg-danger-subtle">
          <span className="text-danger">Cancel User Subscription</span>
        </ModalHeader>
        <ModalBody>
          <div className="text-center">
            <i className="ri-error-warning-line text-danger" style={{ fontSize: "4rem" }}></i>
            <h5 className="mt-3">Are you sure you want to cancel this user?</h5>
            {userToCancel && (
              <div className="mt-3 p-3 bg-light rounded text-start">
                <p className="mb-1"><strong>Email:</strong> {userToCancel.email}</p>
                <p className="mb-1"><strong>User ID:</strong> <code>{userToCancel.id}</code></p>
                <p className="mb-0"><strong>Step:</strong> {userToCancel.failedPaymentStep || 1}</p>
              </div>
            )}
            <Alert color="warning" className="mt-3 mb-0 text-start">
              <strong>This will:</strong>
              <ul className="mb-0 mt-2">
                <li>Cancel schedule in DEX</li>
                <li>Update status in Firebase</li>
                <li>Terminate client eligibility in MFP</li>
                <li>Add to cancelled emails list</li>
              </ul>
            </Alert>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={handleCloseCancelModal} disabled={cancelling}>
            Close
          </Button>
          <Button color="danger" onClick={handleConfirmCancel} disabled={cancelling}>
            {cancelling ? (
              <>
                <Spinner size="sm" className="me-2" />
                Cancelling...
              </>
            ) : (
              <>
                <i className="ri-close-circle-line me-1"></i>
                Confirm Cancel
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Clear Status Modal */}
      <Modal isOpen={clearModalOpen} toggle={handleCloseClearModal} centered>
        <ModalHeader toggle={handleCloseClearModal} className="bg-info-subtle">
          <span className="text-info">Clear Failed Payment Status</span>
        </ModalHeader>
        <ModalBody>
          <div className="text-center">
            <i className="ri-eraser-line text-info" style={{ fontSize: "4rem" }}></i>
            <h5 className="mt-3">Clear failed payment status?</h5>
            {userToClear && (
              <div className="mt-3 p-3 bg-light rounded text-start">
                <p className="mb-1"><strong>Email:</strong> {userToClear.email}</p>
                <p className="mb-0"><strong>Current Step:</strong> {userToClear.failedPaymentStep || 1}</p>
              </div>
            )}
            <div className="mt-3 text-start">
              <label className="form-label">Reason (required)</label>
              <Input
                type="textarea"
                rows="3"
                placeholder="e.g., Paid in cash, Manual payment received..."
                value={clearReason}
                onChange={(e) => setClearReason(e.target.value)}
              />
            </div>
            <Alert color="info" className="mt-3 mb-0 text-start">
              This will clear the failed payment tracking without retrying the payment.
              Use this when the user has paid through other means.
            </Alert>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={handleCloseClearModal} disabled={clearing}>
            Close
          </Button>
          <Button color="info" onClick={handleConfirmClear} disabled={clearing || !clearReason.trim()}>
            {clearing ? (
              <>
                <Spinner size="sm" className="me-2" />
                Clearing...
              </>
            ) : (
              <>
                <i className="ri-eraser-line me-1"></i>
                Clear Status
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ScheduleMonitoring;
